import { ErrorMessage } from "@hookform/error-message";
import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { NumericFormat } from "react-number-format";
import { toast } from "react-toastify";
import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
} from "reactstrap";
import {
  Block,
  BlockBetween,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Button,
  Icon,
  RSelect,
} from "../../components/Component";
import Table from "../../components/react-table/Table";
import { DELETE, GET, POST, PUT } from "../../helper/api";
import Content from "../../layout/content/Content";
import Head from "../../layout/head/Head";
import { INGREDIENT_CATEGORY_ID } from "../../utils/Utils";

const ROUTE = "/processing/unit-price";

const DEFAULT_FORM = {
  ingredientId: "",
  amount: "",
};

const ProcessingUnitPriceManager = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editId, setEditId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [ingredientOptions, setIngredientOptions] = useState([]);
  const [view, setView] = useState({ add: false, edit: false, isDelete: false });

  const {
    handleSubmit,
    reset,
    control,
    errors,
  } = useForm({ defaultValues: DEFAULT_FORM });

  const fetchData = () => {
    setLoading(true);
    GET(ROUTE, {}, true)
      .then(({ data: res }) => setData(res?.rows || []))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchData();
    GET("ingredient", {}, true).then(({ data: res }) => {
      setIngredientOptions(
        (res?.rows || [])
          .filter((c) => !!INGREDIENT_CATEGORY_ID[c.id])
          .map((c) => ({ label: c.name, value: c.id }))
      );
    });
  }, []);

  const toggle = (type) => setView((prev) => ({ ...prev, [type]: !prev[type] }));

  const closeModal = () => {
    setView({ add: false, edit: false, isDelete: false });
    setEditId(null);
    reset(DEFAULT_FORM);
  };

  const onEditClick = (item) => {
    setEditId(item.id);
    reset({
      ingredientId: item.ingredientId,
      amount: item.amount ?? "",
    });
    toggle("edit");
  };

  const onSubmit = (formData) => {
    const payload = {
      ingredientId: formData.ingredientId,
      amount: Number(formData.amount) || 0,
    };

    setLoading(true);
    const request = editId
      ? PUT(`${ROUTE}/${editId}`, payload, {}, true)
      : POST(ROUTE, payload, {}, true);

    request
      .then(() => {
        toast.success(editId ? "Cập nhật thành công" : "Thêm mới thành công");
        closeModal();
        fetchData();
      })
      .catch(({ response }) => {
        toast.error(response?.data?.error || "Có lỗi xảy ra! Vui lòng thử lại.");
      })
      .finally(() => setLoading(false));
  };

  const onDeleteConfirm = () => {
    if (!deleteId) return;
    DELETE(`${ROUTE}/${deleteId}`, {}, true)
      .then(() => {
        toast.success("Xóa thành công");
        closeModal();
        fetchData();
      })
      .catch(({ response }) => {
        toast.error(response?.data?.error || "Có lỗi xảy ra! Vui lòng thử lại.");
      });
  };

  const columns = [
    {
      Header: "Nguyên liệu",
      accessor: (item) => `${item.ingredientId} - ${item.ingredient?.name || ""}`,
    },
    {
      Header: "Đơn giá gia công (đ/kg)",
      accessor: (item) => (
        <div className="text-right">
          <NumericFormat displayType="text" value={item.amount} thousandSeparator="," decimalScale={2} />
        </div>
      ),
    },
    {
      Header: "Hành động",
      accessor: (item) => (
        <ul className="nk-tb-actions gx-1">
          <li>
            <UncontrolledDropdown>
              <DropdownToggle tag="a" className="btn btn-icon dropdown-toggle btn-trigger">
                <Icon name="more-h" />
              </DropdownToggle>
              <DropdownMenu right>
                <ul className="link-list-opt no-bdr">
                  <li>
                    <DropdownItem
                      tag="a"
                      href="#edit"
                      onClick={(e) => { e.preventDefault(); onEditClick(item); }}
                    >
                      <Icon name="edit" />
                      <span>Cập nhật</span>
                    </DropdownItem>
                  </li>
                  <li>
                    <DropdownItem
                      tag="a"
                      href="#delete"
                      onClick={(e) => {
                        e.preventDefault();
                        setDeleteId(item.id);
                        toggle("isDelete");
                      }}
                    >
                      <Icon name="trash" className="text-danger" />
                      <span>Xóa</span>
                    </DropdownItem>
                  </li>
                </ul>
              </DropdownMenu>
            </UncontrolledDropdown>
          </li>
        </ul>
      ),
    },
  ];

  const renderFormFields = () => (
    <>
      <div className="form-group">
        <label className="form-label">
          Nguyên liệu <span className="text-danger">*</span>
        </label>
        <Controller
          name="ingredientId"
          control={control}
          rules={{ required: "Vui lòng chọn nguyên liệu" }}
          render={({ onChange, value, name, ref }) => (
            <RSelect
              ref={ref}
              name={name}
              options={ingredientOptions}
              value={ingredientOptions.find((c) => c.value === value) || null}
              onChange={(val) => onChange(val?.value)}
            />
          )}
        />
        <ErrorMessage errors={errors} name="ingredientId" render={({ message }) => (
          <span className="text-danger fs-11px">{message}</span>
        )} />
      </div>
      <div className="form-group">
        <label className="form-label">
          Đơn giá gia công (đ/kg) <span className="text-danger">*</span>
        </label>
        <Controller
          name="amount"
          control={control}
          rules={{
            required: "Vui lòng nhập đơn giá",
            min: { value: 0, message: "Giá trị phải >= 0" },
          }}
          render={({ onChange, value }) => (
            <NumericFormat
              className="form-control"
              placeholder="Ví dụ: 500"
              thousandSeparator=","
              decimalScale={2}
              value={value}
              onValueChange={(values) => onChange(values.floatValue ?? "")}
            />
          )}
        />
        <ErrorMessage errors={errors} name="amount" render={({ message }) => (
          <span className="text-danger fs-11px">{message}</span>
        )} />
      </div>
    </>
  );

  return (
    <React.Fragment>
      <Head title="Đơn giá gia công" />
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle page>Đơn giá gia công</BlockTitle>
            </BlockHeadContent>
            <BlockHeadContent>
              <Button
                color="primary"
                onClick={() => { reset(DEFAULT_FORM); toggle("add"); }}
              >
                <Icon name="plus" />
                <span>Thêm đơn giá</span>
              </Button>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>

        <Block>
          <div className="card card-bordered card-preview">
            <div className="card-inner">
              <div className="table-full-width">
                <Table loading={loading} columns={columns} data={data} notShowPagi />
              </div>
            </div>
          </div>
        </Block>

        {/* Add/Edit Modal */}
        <Modal isOpen={view.add || view.edit} toggle={closeModal} size="md">
          <ModalHeader toggle={closeModal}>{editId ? "Cập nhật đơn giá gia công" : "Thêm đơn giá gia công"}</ModalHeader>
          <form onSubmit={handleSubmit(onSubmit)}>
            <ModalBody>{renderFormFields()}</ModalBody>
            <ModalFooter>
              <Button type="submit" color="primary" disabled={loading}>
                {loading ? "Đang lưu..." : editId ? "Cập nhật" : "Lưu"}
              </Button>
              <Button type="button" color="light" onClick={closeModal}>Hủy</Button>
            </ModalFooter>
          </form>
        </Modal>

        {/* Delete confirmation Modal */}
        <Modal isOpen={view.isDelete} toggle={() => toggle("isDelete")} size="sm">
          <ModalHeader toggle={() => toggle("isDelete")}>Xác nhận xóa</ModalHeader>
          <ModalBody>
            <p>Bạn có chắc chắn muốn xóa đơn giá gia công này không?</p>
          </ModalBody>
          <ModalFooter>
            <Button color="danger" onClick={onDeleteConfirm}>Xóa</Button>
            <Button color="light" onClick={closeModal}>Hủy</Button>
          </ModalFooter>
        </Modal>
      </Content>
    </React.Fragment>
  );
};

export default ProcessingUnitPriceManager;
